import React, { useState, useEffect } from "react";
import StepCard from "./StepCard";
import QuestionsForm from "./QuestionsForm";
import FeedbackModal from "./FeedbackModal";
import { getQuestions } from "./questionDataService";
import { useNavigate } from "react-router-dom";

const StepTracker = () => {
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Helper function to calculate months difference
  const getMonthsDifference = (taxYearEndDate, dueDate) => {
    if (!taxYearEndDate || !dueDate) return null;
    const start = new Date(taxYearEndDate);
    const end = new Date(dueDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const months = (diffDays / 30).toFixed(1);
    return `${months} months`;
  };

  const [steps, setSteps] = useState([
    { step: 1, title: "Initial Information Required", status: "in-progress", progress: 0, answers: {}, comments: {}, file: null, files: [] },
    { step: 2, title: "Tax Return Preparation", status: "pending", progress: 0 },
    { step: 3, title: "Draft Tax Return", status: "pending", progress: 0 },
    { step: 4, title: "Tax Filing", status: "pending", progress: 0 },
  ]);

  const [memberSteps, setMemberSteps] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [memberFormVisible, setMemberFormVisible] = useState({});
  const [assignedForm, setAssignedForm] = useState("1040");
  const [memberForms, setMemberForms] = useState({});
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [memberTotalQuestions, setMemberTotalQuestions] = useState({});
  const [userData, setUserData] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        setUserData(data.user);
        setAssignedForm(data.user.assignedForm || "1040");

        // Initialize member forms
        const initialMemberForms = {};
        const initialMemberSteps = [];
        const initialMemberTotalQuestions = {};

        if (data.user.members) {
          for (let index = 0; index < data.user.members.length; index++) {
            const member = data.user.members[index];
            initialMemberForms[index] = member.assignedForm || "1040";
            if (member.steps && member.steps.length > 0) {
              // Ensure all member steps have the files property for backward compatibility
              initialMemberSteps[index] = member.steps.map(step => ({
                ...step,
                files: step.files || []
              }));
            } else {
              initialMemberSteps[index] = [
                { step: 1, title: "Initial Information Required", status: "in-progress", progress: 0, answers: {}, comments: {}, file: null, files: [] },
                { step: 2, title: "Tax Return Preparation", status: "pending", progress: 0 },
                { step: 3, title: "Draft Tax Return", status: "pending", progress: 0 },
                { step: 4, title: "Tax Filing", status: "pending", progress: 0 },
              ];
            }

            // Fetch questions for member form to calculate total
            try {
              const memberFormQuestions = await getQuestions(member.assignedForm || "1040");
              initialMemberTotalQuestions[index] = Object.values(memberFormQuestions).reduce(
                (total, section) => total + section.length,
                0
              );
            } catch (error) {
              console.error(`Error fetching questions for member ${index}:`, error);
              initialMemberTotalQuestions[index] = 0;
            }
          }
        }

        setMemberForms(initialMemberForms);
        console.log('Loading member steps with files:', initialMemberSteps);
        setMemberSteps(initialMemberSteps);
        setMemberTotalQuestions(initialMemberTotalQuestions);

        // Calculate total questions for primary form
        try {
          const formQuestions = await getQuestions(data.user.assignedForm || "1040");
          const total = Object.values(formQuestions).reduce((total, section) => total + section.length, 0);
          setTotalQuestions(total);
        } catch (error) {
          console.error('Error fetching primary form questions:', error);
          setTotalQuestions(0);
        }

        // Set steps from user data if available
        if (data.user.steps && data.user.steps.length > 0) {
          // Ensure all steps have the files property for backward compatibility
          const stepsWithFiles = data.user.steps.map(step => ({
            ...step,
            files: step.files || []
          }));
          console.log('Loading steps with files:', stepsWithFiles);
          setSteps(stepsWithFiles);
        }

        // Show feedback modal if step 5 is completed but feedback not submitted
        if (data.user.steps?.some(s => s.step === 4 && s.status === 'submitted') &&
          (!data.user.feedback || !data.user.feedback.rating)) {
          setShowFeedbackModal(true);
        }
      } catch (error) {
        console.error(error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleStepClick = (stepNumber, memberType = "primary", memberIndex = 0) => {
    if (stepNumber === 4) {
      // Check if all previous steps are submitted
      const stepsToCheck = memberType === "primary" ? steps : memberSteps[memberIndex] || [];
      const allPreviousStepsCompleted = stepsToCheck
        .filter(step => step.step < 4) // Only check steps before 4
        .every(step => step.status === 'submitted' || step.status === 'completed');

      if (!allPreviousStepsCompleted) {
        alert('Please complete all previous steps before accessing the Documents section.');
        return;
      }
      
      // If all previous steps are completed, navigate to documents
      navigate("/documents");
      return;
    }

    if (stepNumber === 1) {
      if (memberType === "primary") {
        if (steps[0].status === "submitted") {
          alert("Step 1 has already been submitted and cannot be reopened.");
          return;
        }
        setIsFormVisible(true);
      } else {
        if (memberSteps[memberIndex] && memberSteps[memberIndex][0].status === "submitted") {
          alert(`Step 1 has already been submitted and cannot be reopened.`);
          return;
        }
        setMemberFormVisible(prev => ({ ...prev, [memberIndex]: true }));
      }
    }

  };

  const handleFormSubmit = async (data, isFinalSubmit, memberType = "primary", memberIndex = 0) => {
    const answeredCount = Object.keys(data.answers).filter((k) => data.answers[k]).length;

    if (memberType === "primary") {
      const updatedSteps = steps.map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            title: s.title,
            status: isFinalSubmit ? "submitted" : answeredCount >= 0 ? "in-progress" : "pending",
            progress: answeredCount,
            answers: data.answers,
            comments: data.comments,
            file: data.file || s.file,
            files: data.files || s.files || [],
          };
        }
        if (s.step === 2 && isFinalSubmit) {
          return {
            ...s,
            title: s.title,
            status: "in-progress",
          };
        }
        return s;
      });

      setSteps(updatedSteps);

      // Save the updated steps to the database
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-steps`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ steps: updatedSteps }),
        });

        if (!response.ok) throw new Error("Failed to update steps");

        if (isFinalSubmit) {
          alert("Form submitted successfully!");
          setIsFormVisible(false);
        }
      } catch (error) {
        console.error(error);
        setError('Failed to save progress. Please try again.');
      }
    } else {
      const updatedMemberSteps = [...memberSteps];
      updatedMemberSteps[memberIndex] = updatedMemberSteps[memberIndex].map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            title: s.title,
            status: isFinalSubmit ? "submitted" : answeredCount >= 0 ? "in-progress" : "pending",
            progress: answeredCount,
            answers: data.answers,
            comments: data.comments,
            file: data.file || s.file,
            files: data.files || s.files || [],
          };
        }
        if (s.step === 2 && isFinalSubmit) {
          return {
            ...s,
            title: s.title,
            status: "in-progress",
          };
        }
        return s;
      });

      setMemberSteps(updatedMemberSteps);

      // Save the updated member steps to the database
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-steps`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            memberIndex,
            steps: updatedMemberSteps[memberIndex]
          }),
        });

        if (!response.ok) throw new Error("Failed to update member steps");

        if (isFinalSubmit) {
          alert(`Member ${memberIndex + 1} form submitted successfully!`);
          setMemberFormVisible(prev => ({ ...prev, [memberIndex]: false }));
        }
      } catch (error) {
        console.error(error);
        setError('Failed to save member progress. Please try again.');
      }
    }
  };

  const handleFeedbackSubmit = async (rating, comment) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: userData._id,
          rating,
          comment
        }),
      });

      if (!response.ok) throw new Error("Failed to submit feedback");

      setUserData(prev => ({
        ...prev,
        feedback: {
          rating,
          comment,
          submittedAt: new Date()
        }
      }));
      setShowFeedbackModal(false);
    } catch (error) {
      console.error(error);
      alert("Failed to submit feedback. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="tax-filing-tracker-container">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading your tax filing information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tax-filing-tracker-container">
        <div className="text-center p-5">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tax-filing-tracker-container">
      <marquee style={{
        backgroundColor: '#1d4b81',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        marginBottom: '20px',
        fontSize: '16px',
        fontWeight: 'bold',
        width: '100%'
      }}>
        {/* <span style={{
          backgroundColor: '#ffc107',
          color: '#000',
          padding: '5px 10px',
          borderRadius: '4px',
          margin: '0 5px'
        }}> </span>  */}
         📋 Start with Upload Documents ➡️ Review & Confirm Checklist ➡️ Review Draft Tax Return ➡️ Form 8879 Confirmation ➡️ Filing tax Return | 💬 Use Chat with Us to communicate.
      </marquee>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', width: '100%' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#333', paddingLeft: '10px', borderBottom: '1px solid #1d4b81', paddingBottom: '10px' }}>
          Form: {assignedForm} : {userData ? userData.name : ''}
          {userData?.taxYearEndDate && (
            <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#666', marginLeft: '15px' }}>
              || Tax Year End: {formatDate(userData.taxYearEndDate)}
            </span>
          )}
          {userData?.dueDate && (
            <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
              || Due Date: {formatDate(userData.dueDate)}
              {(assignedForm === '1040' || assignedForm === '1120') && (
                <span style={{ color: '#1d4b81', fontWeight: '500' }}> (3.5 months)</span>
              )}
              {(assignedForm === '1065' || assignedForm === '1120-S') && (
                <span style={{ color: '#1d4b81', fontWeight: '500' }}> (2.5 months)</span>
              )}
            </span>
          )}
        </h2>
      </div>
      <div className="tax-filing-step-grid">
        {steps.map((s) => (
          <StepCard
            key={`primary-${s.step}`}
            step={s.step}
            title={s.title}
            status={s.status}
            progress={s.progress}
            totalQuestions={totalQuestions}
            onClick={() => handleStepClick(s.step)}
          />
        ))}
      </div>



      {isFormVisible && (
        <QuestionsForm
          onClose={() => setIsFormVisible(false)}
          onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal, "primary")}
          initialAnswers={steps[0].answers}
          initialComments={steps[0].comments}
          initialFiles={(() => {
            console.log('Primary step files:', steps[0].files);
            return steps[0].files || [];
          })()}
          isSubmitted={steps[0].status === "submitted"}
          formNumber={assignedForm}
        />
      )}

      {/* Render member forms */}
      {userData?.members?.map((member, index) => (
        <React.Fragment key={`member-${index}`}>
          <br />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', width: '100%' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#333', paddingLeft: '10px', borderBottom: '1px solid #1d4b81', paddingBottom: '10px' }}>
              Form: {memberForms[index] || "1040"} : {member.name || `Member ${index + 1}`}
              {member?.taxYearEndDate && (
                <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#666', marginLeft: '15px' }}>
                  || Tax Year End: {formatDate(member.taxYearEndDate)}
                </span>
              )}
              {member?.dueDate && (
                <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                  || Due Date: {formatDate(member.dueDate)}
                  {(memberForms[index] === '1040' || memberForms[index] === '1120') && (
                    <span style={{ color: '#1d4b81', fontWeight: '500' }}> (3.5 months)</span>
                  )}
                  {(memberForms[index] === '1065' || memberForms[index] === '1120-S') && (
                    <span style={{ color: '#1d4b81', fontWeight: '500' }}> (2.5 months)</span>
                  )}
                </span>
              )}
            </h2>
          </div>
          <div className="tax-filing-step-grid">
            {memberSteps[index]?.map((s) => (
              <StepCard
                key={`member-${index}-${s.step}`}
                step={s.step}
                title={s.title}
                status={s.status}
                progress={s.progress}
                totalQuestions={memberTotalQuestions[index] || 0}
                onClick={() => handleStepClick(s.step, "member", index)}
              />
            ))}
          </div>

           {/* Bundle Activation Message */}
          {userData?.isBundlePurchased && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
              <div style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '8px',
                marginTop: '15px',
                fontSize: '15px',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>✅</span>
                <span>Quarterly Tax Payment Activated</span>
              </div>
            </div>
          )}

          {memberFormVisible[index] && (
            <QuestionsForm
              onClose={() => setMemberFormVisible(prev => ({ ...prev, [index]: false }))}
              onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal, "member", index)}
              initialAnswers={memberSteps[index]?.[0]?.answers || {}}
              initialComments={memberSteps[index]?.[0]?.comments || {}}
              initialFiles={(() => {
                console.log(`Member ${index} step files:`, memberSteps[index]?.[0]?.files);
                return memberSteps[index]?.[0]?.files || [];
              })()}
              isSubmitted={memberSteps[index]?.[0]?.status === "submitted"}
              formNumber={memberForms[index] || "1040"}
              memberType="member"
              memberIndex={index}
              memberName={member.name}
            />
          )}
        </React.Fragment>
      ))}

      <br></br>

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
};

export default StepTracker;